import { useGetContactsSettingsQuery } from 'store/services/core/settings';
import { useGetModulesWithAgendasQuery } from 'store/services/core/modules';
import { useHeliosTemplateChildren } from 'utils/hooks/useHeliosTemplateChildren';
import { resolveAgendaName } from 'utils/resolveAgendaName';
import { memo, useCallback, useMemo } from 'react';
import { MenuItem } from './MenuItem';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { Divider, SkeletonRow, Subtitle } from './UserMenu.styles';

import { DropdownMenuItem } from './DropdownMenuItem';
import { isPathActive } from './isPathActive';
import type { IconNames } from '../Icon';
import { HeliosTemplateChildrenContext } from 'contexts/HeliosTemplateChildrenContext';
import { useLocation } from 'react-router-dom';
import { DATA_STRUCTURE } from 'types/enums';
import { useFeatureFlag } from 'utils/hooks/posthog/useFeatureFlag';
import { useMenuNavigation } from './useMenuNavigation';
import { toTestIdSegment } from 'utils/testId';
import { useNotificationsCount } from './hooks/useNotificationsCount';
import type { Module } from 'types/modules';

type NavigationItemData = {
  type: 'link';
  icon: string;
  text: string;
  link: string;
  dataTestId?: string;
  unreadNotificationsCount?: number;
  isNav?: boolean;
};

type ModuleItemData = {
  type: 'module';
  moduleId: string;
  name: string;
  icon: IconNames;
  path: string;
  dataTestId: string;
  links: { name: string; path: string; unreadNotificationsCount: number }[];
  ownModuleId?: string;
  childModules?: Omit<Module, 'childs'>[];
  unreadNotificationsCount?: number;
};

type MixedItemData = NavigationItemData | ModuleItemData;

const mapModuleToData = (module: Module): ModuleItemData => {
  const modulePath = `/user/modules/${module.moduleId}`;
  return {
    type: 'module',
    moduleId: module.moduleId,
    name: module.name,
    icon: module.icon as IconNames,
    path: modulePath,
    dataTestId: `sidebar-user-module-${toTestIdSegment(module.moduleId)}`,
    links: module.agendas
      .filter((agenda) => !agenda.hideInOverview)
      .map((agenda) => ({
        name: resolveAgendaName(agenda),
        path: `${modulePath}/${agenda.systemType}/${agenda.agendaId}`,
        unreadNotificationsCount: agenda.unreadNotificationsCount,
      })),
    ownModuleId: module.moduleId,
    childModules: module.childs,
    unreadNotificationsCount: module.unreadNotificationsCount,
  };
};

const MenuSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <SkeletonRow key={i}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width="70%" />
      </SkeletonRow>
    ))}
  </>
);

export const UserMenu = memo(({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigateFromMenu = useMenuNavigation();
  const enabledUserDashboard = Boolean(useFeatureFlag('F20-2625'));

  const { data, isLoading } = useGetModulesWithAgendasQuery({ takeAgendas: 0, takeModules: 0, dataStructure: DATA_STRUCTURE.HIERARCHICAL });
  const { data: contactsSettingsData, isLoading: isContactsSettingsLoading } = useGetContactsSettingsQuery();
  const { infoNotifications, actionNotifications } = useNotificationsCount({ enabledUserDashboard });
  const heliosTemplateChildrenContextValue = useHeliosTemplateChildren(data?.modules);

  const handleNavigateTo = useCallback((link: string) => navigateFromMenu(link), [navigateFromMenu]);

  // Build the data configuration ONCE, irrespective of current pathname.
  const userItemsData = useMemo<MixedItemData[]>(() => {
    if (isContactsSettingsLoading) return [];

    const pinnedModules = data?.modules?.filter((module) => module.isPinned) ?? [];
    const modulesData: ModuleItemData[] = pinnedModules.map(mapModuleToData);

    const navLinks: NavigationItemData[] = [
      { type: 'link', icon: 'Home', text: t('sidebar.user.overview'), link: '/user', dataTestId: 'sidebar-user-overview', isNav: true },
      {
        type: 'link',
        icon: 'Window',
        text: t('sidebar.user.applicationsAndModules'),
        link: '/user/modules',
        dataTestId: 'sidebar-user-applications-modules',
        isNav: true,
      },
      ...(enabledUserDashboard
        ? [
            {
              type: 'link' as const,
              icon: 'PlaylistAddCheck',
              text: t('sidebar.user.info'),
              link: '/user/notifications/info',
              dataTestId: 'sidebar-user-info',
              unreadNotificationsCount: infoNotifications.length,
              isNav: true as const,
            },
            {
              type: 'link' as const,
              icon: 'Checklist',
              text: t('sidebar.user.action'),
              link: '/user/notifications/action',
              dataTestId: 'sidebar-user-action',
              unreadNotificationsCount: actionNotifications.length,
              isNav: true as const,
            },
          ]
        : []),
    ];

    const otherLinks: NavigationItemData[] = [
      ...(contactsSettingsData?.enableContacts
        ? [
            {
              type: 'link' as const,
              icon: 'Contacts',
              text: contactsSettingsData.displayContactsAs,
              link: '/user/contacts',
              dataTestId: 'sidebar-user-contacts',
            },
          ]
        : []),
    ];

    const mixed: MixedItemData[] = [...navLinks, ...modulesData, ...otherLinks];

    return mixed;
  }, [
    isContactsSettingsLoading,
    data?.modules,
    t,
    enabledUserDashboard,
    infoNotifications.length,
    actionNotifications.length,
    contactsSettingsData?.enableContacts,
    contactsSettingsData?.displayContactsAs,
  ]);

  const regularModulesData = useMemo<ModuleItemData[]>(() => {
    if (isLoading || !data) return [];

    return data.modules.filter((module) => !module.isPinned).map(mapModuleToData);
  }, [data, isLoading]);

  // RENDER PHASE - Fast map over the data, injecting the fast-changing `pathname` prop.
  const renderNavItems = useMemo(() => {
    if (isContactsSettingsLoading) return <MenuSkeleton rows={3} />;

    const navItems = userItemsData.filter((item): item is NavigationItemData => item.type === 'link' && item.isNav === true);

    return (
      <div data-testid="sidebar-nav-group">
        {navItems.map((item) => (
          <MenuItem
            key={item.link}
            open={open}
            active={item.link === pathname}
            onClick={() => handleNavigateTo(item.link)}
            icon={item.icon as IconNames}
            text={item.text}
            dataTestId={item.dataTestId}
            unreadNotificationsCount={item.unreadNotificationsCount}
          />
        ))}
      </div>
    );
  }, [isContactsSettingsLoading, userItemsData, open, pathname, handleNavigateTo]);

  const renderOtherItems = useMemo(() => {
    if (isContactsSettingsLoading) return <MenuSkeleton rows={3} />;

    return userItemsData
      .filter((item) => !(item.type === 'link' && item.isNav === true))
      .map((item) => {
        if (item.type === 'module') {
          return (
            <DropdownMenuItem
              key={item.moduleId}
              text={item.name}
              open={open}
              icon={item.icon}
              path={item.path}
              dataTestId={item.dataTestId}
              links={item.links}
              ownModuleId={item.ownModuleId}
              childModules={item.childModules}
              openWhenClick
              pathname={pathname}
              active={isPathActive(pathname, item.path)}
              unreadNotificationsCount={item.unreadNotificationsCount}
            />
          );
        }

        return (
          <MenuItem
            key={item.link}
            open={open}
            active={item.link === pathname}
            onClick={() => handleNavigateTo(item.link)}
            icon={item.icon as IconNames}
            text={item.text}
            dataTestId={item.dataTestId}
            unreadNotificationsCount={item.unreadNotificationsCount}
          />
        );
      });
  }, [isContactsSettingsLoading, userItemsData, open, pathname, handleNavigateTo]);

  const renderModules = useMemo(() => {
    if (isLoading || !data) return <MenuSkeleton rows={4} />;

    return regularModulesData.map((item) => (
      <DropdownMenuItem
        key={item.moduleId}
        text={item.name}
        open={open}
        icon={item.icon}
        path={item.path}
        dataTestId={item.dataTestId}
        links={item.links}
        ownModuleId={item.ownModuleId}
        childModules={item.childModules}
        openWhenClick
        pathname={pathname}
        active={isPathActive(pathname, item.path)}
        unreadNotificationsCount={item.unreadNotificationsCount}
      />
    ));
  }, [isLoading, data, regularModulesData, open, pathname]);

  return (
    <HeliosTemplateChildrenContext.Provider value={heliosTemplateChildrenContextValue}>
      {renderNavItems}
      <Divider data-testid="sidebar-divider-nav" />
      {renderOtherItems}
      <Divider data-testid="sidebar-divider" />
      {open && <Subtitle data-testid="sidebar-user-your-modules">{t('sidebar.user.yourModules')}</Subtitle>}
      {renderModules}
    </HeliosTemplateChildrenContext.Provider>
  );
});
